<template>
  <div class="feature-flags-management-container">
    <!-- Page Header -->
    <div class="page-header py-4">
      <div class="header-content">
        <div class="header-left">
          <h1 class="page-title">Feature Configuration</h1>
          <p class="page-subtitle">Granular control over system capabilities and rollout status</p>
        </div>
        <div class="header-right">
        </div>
      </div>
    </div>

    <!-- Error Alert -->
    <div v-if="validation" class="alert-box-ext mb-4">
      <i class="fas fa-exclamation-circle me-2"></i>
      {{ validation }}
    </div>

    <!-- Main Card Container -->
    <div class="management-card">
      <!-- Filter Bar -->
      <div class="filter-bar">
        <div class="filter-left">
          <!-- Global Search -->
          <div class="filter-group flex-grow-1">
            <div class="section-label-tabs">
              <span class="label-tab">Search Feature</span>
            </div>
            <div class="search-input-wrapper-ext">
              <input type="text" v-model="globalSearchQuery" class="form-control-ext-search" placeholder="Search by name, description..." @input="handleGlobalSearch" />
              <i class="fas fa-search search-icon-right"></i>
            </div>
          </div>
        </div>
        <div class="filter-right">
          <button class="btn-info-filter" @click="showHowItWorks = true" title="How It Works">How It Works</button>
          <div class="stats-summary">
            <span class="stats-label">Total Flags:</span>
            <span class="stats-value">{{ totalFlagsCount }}</span>
          </div>
          <button class="btn-reset-primary" @click="clearAllFilters" title="Reset All Filters">
            <i class="fas fa-undo-alt"></i>
          </button>
        </div>
      </div>

      <!-- Content Area -->
      <div class="card-content">
        <div v-if="page.isLoading" class="loading-state">
          <div class="loader-box">
            <div class="spinner-border text-danger" role="status"></div>
            <p>Scanning feature flag matrix...</p>
          </div>
        </div>

        <div v-else-if="!activeCorporateId" class="empty-state-container">
          <div class="empty-art"><i class="fas fa-building"></i></div>
          <h3>No Corporate Selected</h3>
          <p>Please select a corporate from the navigation to view and manage feature flags.</p>
        </div>

        <div v-else-if="totalFlagsCount === 0 && !page.isLoading" class="empty-flags-container">
          <div class="empty-flags-illustration">
            <img src="/img/admin-icon/empty-feature-flagss.svg" alt="No feature flags" width="180" height="230" />
          </div>
          <p class="empty-flags-title">This corporate does not have any registered flags yet.</p>
          <p class="empty-flags-sub">Please add a flag to start managing data</p>
        </div>

        <div v-else class="sections-container">
          <feature-flag-section v-if="filteredSystemFlags.length > 0" title="System Flags" icon="fas fa-cogs" description="DESCRIPTION" status-label="STATUS" actions-label="ACTIONS" :flags="filteredSystemFlags" :all-flags="allFlagsFlat" :search-query="globalSearchQuery" :toggle-loading="toggleLoading" :toggle-reset-counter="toggleResetCounter" :local-changes="pendingChanges" @toggle="handleToggleClick" @view-audit="viewAudit" @view-detail="viewDetail" />
          <feature-flag-section v-if="filteredCorporateFlags.length > 0" title="Corporate Dashboard" icon="fas fa-building" description="DESCRIPTION" status-label="STATUS" actions-label="ACTIONS" :flags="filteredCorporateFlags" :all-flags="allFlagsFlat" :search-query="globalSearchQuery" :toggle-loading="toggleLoading" :toggle-reset-counter="toggleResetCounter" :local-changes="pendingChanges" @toggle="handleToggleClick" @view-audit="viewAudit" @view-detail="viewDetail" />
          <feature-flag-section v-if="filteredBranchFlags.length > 0" title="Branch Dashboard" icon="fas fa-code-branch" description="DESCRIPTION" status-label="STATUS" actions-label="ACTIONS" :flags="filteredBranchFlags" :all-flags="allFlagsFlat" :search-query="globalSearchQuery" :toggle-loading="toggleLoading" :toggle-reset-counter="toggleResetCounter" :local-changes="pendingChanges" @toggle="handleToggleClick" @view-audit="viewAudit" @view-detail="viewDetail" />
          <feature-flag-section v-if="filteredMerchantFlags.length > 0" title="Merchant Dashboard" icon="fas fa-store" description="DESCRIPTION" status-label="STATUS" actions-label="ACTIONS" :flags="filteredMerchantFlags" :all-flags="allFlagsFlat" :search-query="globalSearchQuery" :toggle-loading="toggleLoading" :toggle-reset-counter="toggleResetCounter" :local-changes="pendingChanges" @toggle="handleToggleClick" @view-audit="viewAudit" @view-detail="viewDetail" />

          <div v-if="totalFlagsCount > 0 && !hasAnyFilteredFlags" class="no-results-state">
            <div class="empty-art"><i class="fas fa-search"></i></div>
            <h3>No Flags Found</h3>
            <p>No feature flags match your search criteria. Try adjusting your search.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Pending Changes Footer -->
    <transition name="footer-slide">
      <div v-if="hasChanges" class="pending-footer">
        <div class="pending-footer-content">
          <div class="pending-footer-info">
            <i class="fas fa-exclamation-circle me-2"></i>
            <span>You have <strong>{{ Object.keys(pendingChanges).length }}</strong> unsaved change{{ Object.keys(pendingChanges).length !== 1 ? 's' : '' }}</span>
          </div>
          <div class="pending-footer-actions">
            <button class="btn-discard-changes" @click="discardChanges" :disabled="isSaving">
              <i class="fas fa-times me-2"></i>Discard
            </button>
            <button class="btn-save-changes" @click="saveChanges" :disabled="isSaving">
              <span v-if="isSaving"><i class="fas fa-spinner fa-spin me-2"></i>Saving...</span>
              <span v-else><i class="fas fa-save me-2"></i>Save Changes</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Confirmation Modal -->
    <o-modal v-if="showConfirmModal" @close="discardToggle">
      <template v-slot:header>
        <h5 class="modal-title fw-bold">Confirm Flag Update</h5>
      </template>
      <template v-slot:body>
        <div class="confirm-modal-content">
          <div class="confirm-icon" :class="pendingToggle.newValue ? 'enable' : 'disable'">
            <i class="fas" :class="pendingToggle.newValue ? 'fa-check' : 'fa-times'"></i>
          </div>
          <p class="confirm-text">
            Are you sure you want to <strong>{{ pendingToggle.newValue ? 'ENABLE' : 'DISABLE' }}</strong> the following feature?
          </p>
          <div class="flag-info-card">
             <div class="flag-info-key">{{ pendingToggle.flag?.name }}</div>
             <div class="flag-info-desc">{{ pendingToggle.flag?.description }}</div>
          </div>
          <p class="text-muted small mt-3">This change will take effect immediately for all users in the selected tenant cluster.</p>
        </div>
      </template>
      <template v-slot:footer>
        <button type="button" class="btn btn-light-ext" @click="discardToggle">Discard</button>
        <button type="button" class="btn" :class="pendingToggle.newValue ? 'btn-success-ext' : 'btn-danger-ext'" @click="confirmToggle">
          Yes, Update Flag
        </button>
      </template>
    </o-modal>

    <!-- How It Works Modal -->
    <div v-if="showHowItWorks" class="modal-backdrop" @click.self="showHowItWorks = false" style="z-index: 1050; position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(15, 23, 42, 0.4); display: flex; align-items: center; justify-content: center;">
      <div class="card" style="width: 95%; max-width: 800px; animation: slideIn 0.3s ease-out; background: white; border-radius: 12px; overflow: hidden; border: none; box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);">
        <div class="card-header border-bottom d-flex justify-content-between align-items-center p-4" style="background: white;">
          <h5 class="m-0 fw-bold text-dark"><i class="fas fa-info-circle text-primary me-2"></i>How It Works</h5>
          <button class="btn-close" @click="showHowItWorks = false"></button>
        </div>
        <div class="card-body p-4 text-start bg-white">
          <div class="row gx-5">
            <div class="col-md-6 mb-4 mb-md-0">
              <div class="mb-4"><strong style="font-size: 13px; color: #1e293b;">1. Corporate Master Control:</strong><br><span class="text-muted" style="font-size: 13px;">Define the core functional modules enabled for this corporate entity.</span></div>
              <div class="mb-4"><strong style="font-size: 13px; color: #1e293b;">2. Automated Inheritance:</strong><br><span class="text-muted" style="font-size: 13px;">Connected branches and merchants instantly adopt the corporate feature settings.</span></div>
              <div class="mb-0"><strong style="font-size: 13px; color: #1e293b;">3. Real-time UI Adaptation:</strong><br><span class="text-muted" style="font-size: 13px;">UI changes, such as menu visibility, are reflected immediately across all affected tenant dashboards.</span></div>
            </div>
            <div class="col-md-6">
              <div class="scenario-box shadow-sm">
                <div class="scenario-header">
                  <span class="scenario-title">Tenant Cascade Example</span>
                  <span class="scenario-config">Module: <strong>Marketing</strong></span>
                </div>
                <div class="scenario-timeline">
                  <div class="timeline-step success"><div class="timeline-marker"></div><div class="step-content"><span class="step-label">Corporate Action:</span><span class="step-desc">Admin activates Marketing for 'Jeevawasa'.</span></div></div>
                  <div class="timeline-step info"><div class="timeline-marker" style="border-color: #3b82f6;"></div><div class="step-content"><span class="step-label">System Process:</span><span class="step-desc">System deploys flag to all connected sub-tenants.</span></div></div>
                  <div class="timeline-step warning"><div class="timeline-marker" style="border-color: #64748b;"></div><div class="step-content"><span class="step-label">Affected Tenants:</span><div class="step-note"><i class="fas fa-building me-1 opacity-75"></i>12 Branches, 45 Merchants</div></div></div>
                  <div class="timeline-step danger"><div class="timeline-marker" style="border-color: #ef4444;"></div><div class="step-content"><span class="step-label">Resulting State:</span><span class="step-desc">Bali Branch Admin sees 'Marketing' menu.</span></div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import api from '@/helpers/api'
import { OModal } from '@/components/OmnihControl'
import FeatureFlagSection from '@/components/extranet/FeatureFlagSection.vue'
import { useFeatureFlagConfig } from '@/composables/useFeatureFlagConfig'
import { useToast } from '@/composables/useToast'

export default {
    name: 'ExtranetFeatureFlags',
    
    components: {
        OModal,
        FeatureFlagSection
    },

    setup() {
        const router = useRouter()
        const store = useStore()
        const { showToast } = useToast()
        const { getConfig } = useFeatureFlagConfig()

        const page = reactive({
            isLoading: false
        })

        const corporateFlags = ref([])
        const branchFlags = ref([])
        const merchantFlags = ref([])
        const systemFlags = ref([])

        const validation = ref('')
        const globalSearchQuery = ref('')
        const toggleLoading = ref({})
        const toggleResetCounter = ref({})
        const showConfirmModal = ref(false)
        const showHowItWorks = ref(false)
        const pendingToggle = ref({
            flag: null,
            newValue: false
        })
        const pendingChanges = ref({}) 
        const isSaving = ref(false)

        const activeCorporateId = computed(() => store.state.extranet?.selectedTenant?.id)
        const activeCorporateName = computed(() => store.state.extranet?.selectedTenant?.name || 'No Corporate Selected')

        const filteredCorporateFlags = computed(() => filterFlags(corporateFlags.value))
        const filteredBranchFlags = computed(() => filterFlags(branchFlags.value))
        const filteredMerchantFlags = computed(() => filterFlags(merchantFlags.value))
        const filteredSystemFlags = computed(() => filterFlags(systemFlags.value))

        const allFlagsFlat = computed(() => [
            ...corporateFlags.value, ...branchFlags.value,
            ...merchantFlags.value, ...systemFlags.value
        ])

        const totalFlagsCount = computed(() => {
            const uniqueFeatures = new Set(allFlagsFlat.value.map(f => f.feature))
            return uniqueFeatures.size
        })

        const hasAnyFilteredFlags = computed(() => {
            return filteredCorporateFlags.value.length > 0 ||
                   filteredBranchFlags.value.length > 0 ||
                   filteredMerchantFlags.value.length > 0 ||
                   filteredSystemFlags.value.length > 0
        })
        const hasChanges = computed(() => Object.keys(pendingChanges.value).length > 0)

        function filterFlags(flags) {
            if (!globalSearchQuery.value.trim()) return flags
            const query = globalSearchQuery.value.trim().toLowerCase()
            return flags.filter(flag => 
                flag.name.toLowerCase().includes(query) || 
                (flag.description && flag.description.toLowerCase().includes(query))
            )
        }

        function handleGlobalSearch() { /* reactive computed auto-updates */ }

        function clearAllFilters() {
            globalSearchQuery.value = ''
        }

        async function loadFeatureFlags() {
            if (!activeCorporateId.value) {
                corporateFlags.value = []
                branchFlags.value = []
                merchantFlags.value = []
                systemFlags.value = []
                return
            }

            page.isLoading = true
            validation.value = ''

            try {
                const response = await api.request('get', `/api/extranet/feature-flags/${activeCorporateId.value}`)
                
                if (response.data && response.data.data) {
                    const flagsObj = response.data.data

                    // Auto-disable promotions_module on the UI if both dependencies are OFF
                    const pointFlagData = flagsObj['point_control']
                    const benefitFlagData = flagsObj['benefit_module']
                    const isPointNonActive = !pointFlagData || pointFlagData.flag === 'NONACTIVE'
                    const isBenefitNonActive = !benefitFlagData || benefitFlagData.flag === 'NONACTIVE'
                    
                    if (isPointNonActive && isBenefitNonActive && flagsObj['promotions_module']) {
                        flagsObj['promotions_module'].flag = 'NONACTIVE'
                    }

                    const corporateFlagKeys = [
                        'promotions_module', 'marketing_module', 'newsletter_module',
                        'transaction_report_module', 'benefit_module', 'threshold_setting',
                        'booking_module'
                    ]
                    const branchFlagKeys = ['branch_product_management', 'product_management']
                    const merchantFlagKeys = ['merchant_product', 'voucher_gift', 'merchant_access_token']
                    const systemFlagKeys = ['point_control', 'new_auth_module']

                    const branchInheritedKeys = ['marketing_module', 'newsletter_module', 'benefit_module', 'transaction_report_module']
                    const merchantInheritedKeys = ['marketing_module', 'newsletter_module', 'benefit_module']

                    const corporate = []
                    const branch = []
                    const merchant = []
                    const system = []

                    for (const featureKey in flagsObj) {
                        if (!Object.prototype.hasOwnProperty.call(flagsObj, featureKey)) continue
                        const flagData = flagsObj[featureKey]
                        if (typeof flagData !== 'object' || !flagData.feature) continue

                        const formatted = formatFlagData(flagData)

                        if (systemFlagKeys.includes(featureKey)) {
                            system.push(formatted)
                        } else if (corporateFlagKeys.includes(featureKey)) {
                            corporate.push(formatted)
                        } else if (branchFlagKeys.includes(featureKey)) {
                            branch.push(formatted)
                        } else if (merchantFlagKeys.includes(featureKey)) {
                            merchant.push(formatted)
                        } else {
                            system.push(formatted)
                        }
                    }

                    branchInheritedKeys.forEach(key => {
                        const flagData = flagsObj[key]
                        if (flagData && typeof flagData === 'object' && flagData.feature) {
                            branch.push(formatFlagData(flagData, true))
                        }
                    })

                    merchantInheritedKeys.forEach(key => {
                        const flagData = flagsObj[key]
                        if (flagData && typeof flagData === 'object' && flagData.feature) {
                            merchant.push(formatFlagData(flagData, true))
                        }
                    })

                    corporateFlags.value = corporate
                    branchFlags.value = branch
                    merchantFlags.value = merchant
                    systemFlags.value = system
                }
            } catch (error) {
                console.error('Error loading feature flags:', error)
                validation.value = error.response?.data?.message || 'Failed to load feature flags'
            } finally {
                page.isLoading = false
            }
        }

        function formatFlagData(flagData, isInherited = false) {
            const config = getConfig(flagData.feature)

            const isPointControl = flagData.feature === 'point_control'
            const isEnabled = isPointControl 
                ? (flagData.flag === 'CENTRALIZE' || flagData.flag === 'DECENTRALIZE')
                : flagData.flag === 'ACTIVE'

            return {
                ...flagData,
                name: config?.name || flagData.feature,
                description: config?.description || '',
                is_enabled: isEnabled,
                isInherited: isInherited,
                inheritedFrom: isInherited ? 'Corporate' : null,
                corporate_name: activeCorporateName.value
            }
        }

        function handleToggleClick(flagId, newValue) {
            const flag = allFlagsFlat.value.find(f => f.id === flagId)
            if (!flag || flag.isInherited) return

            queueChange(flagId, flag, newValue)
            // Auto-queue disablement of promotions_module if needed
            if (!newValue && (flag.feature === 'point_control' || flag.feature === 'benefit_module')) {
                const all = allFlagsFlat.value
                const pointFlag  = all.find(f => f.feature === 'point_control')
                const benefitFlag = all.find(f => f.feature === 'benefit_module')
                const promotionsFlag = all.find(f => f.feature === 'promotions_module')
                // Simulate state after this toggle
                const isPointNonActive  = flag.feature === 'point_control'  ? true : (pendingChanges.value[pointFlag?.id] !== undefined ? !pendingChanges.value[pointFlag?.id] : !pointFlag?.is_enabled)
                const isBenefitNonActive = flag.feature === 'benefit_module' ? true : (pendingChanges.value[benefitFlag?.id] !== undefined ? !pendingChanges.value[benefitFlag?.id] : !benefitFlag?.is_enabled)
                const isPromotionsOn = promotionsFlag ? (pendingChanges.value[promotionsFlag?.id] !== undefined ? pendingChanges.value[promotionsFlag?.id] : promotionsFlag?.is_enabled) : false

                if (isPointNonActive && isBenefitNonActive && isPromotionsOn && promotionsFlag) {
                    queueChange(promotionsFlag.id, promotionsFlag, false)
                    showToast({
                        type: 'warning',
                        title: 'Auto-Disabled',
                        message: 'Promotions Module has been queued to disable due to dependencies.'
                    })
                }
            }

            // Auto-queue disablement of voucher_gift if point_control is disabled
            if (!newValue && flag.feature === 'point_control') {
                const all = allFlagsFlat.value
                const voucherGiftFlag = all.find(f => f.feature === 'voucher_gift')
                const isVoucherGiftOn = voucherGiftFlag ? (pendingChanges.value[voucherGiftFlag?.id] !== undefined ? pendingChanges.value[voucherGiftFlag?.id] : voucherGiftFlag?.is_enabled) : false

                if (isVoucherGiftOn && voucherGiftFlag) {
                    queueChange(voucherGiftFlag.id, voucherGiftFlag, false)
                    showToast({
                        type: 'warning',
                        title: 'Auto-Disabled',
                        message: 'Voucher Gift has been queued to disable due to missing point control.'
                    })
                }
            }

            // Check product flags and show warning
            if (!newValue && (flag.feature === 'branch_product_management' || flag.feature === 'merchant_product')) {
                const all = allFlagsFlat.value
                const branchFlag = all.find(f => f.feature === 'branch_product_management')
                const merchantFlag = all.find(f => f.feature === 'merchant_product')

                const isBranchOff = flag.feature === 'branch_product_management' ? true : (pendingChanges.value[branchFlag?.id] !== undefined ? !pendingChanges.value[branchFlag?.id] : !branchFlag?.is_enabled)
                const isMerchantOff = flag.feature === 'merchant_product' ? true : (pendingChanges.value[merchantFlag?.id] !== undefined ? !pendingChanges.value[merchantFlag?.id] : !merchantFlag?.is_enabled)

                if (isBranchOff && isMerchantOff) {
                    showToast({
                        type: 'warning',
                        title: 'POS Membership Impact',
                        message: 'Disabling both Branch and Merchant Product will make POS Membership unusable for creating transactions.'
                    })
                }
            }
        }


        function discardToggle() {
            if (pendingToggle.value.flag) {
                const flagId = pendingToggle.value.flag.id
                toggleResetCounter.value[flagId] = (toggleResetCounter.value[flagId] || 0) + 1
            }
            showConfirmModal.value = false
            pendingToggle.value = { flag: null, newValue: false }
        }

        function queueChange(flagId, flag, newValue) {
            const updated = { ...pendingChanges.value }
            // If the new value is the same as the original, remove it from pending
            if (flag.is_enabled === newValue) {
                delete updated[flagId]
            } else {
                updated[flagId] = newValue
            }
            pendingChanges.value = updated
        }

        function discardChanges() {
            pendingChanges.value = {}
        }

        async function saveChanges() {
            if (!Object.keys(pendingChanges.value).length) return

            isSaving.value = true
            validation.value = ''

            try {
                const corporateId = activeCorporateId.value
                const entries = Object.entries(pendingChanges.value)

                for (const [flagId, newValue] of entries) {
                    const flag = allFlagsFlat.value.find(f => String(f.id) === String(flagId))
                    if (!flag) continue

                    const toggledFeature = flag.feature
                    let targetFlagStr = ''
                    if (toggledFeature === 'point_control') {
                        targetFlagStr = newValue ? 'DECENTRALIZE' : 'NONACTIVE'
                    } else {
                        targetFlagStr = newValue ? 'ACTIVE' : 'NONACTIVE'
                    }

                    await api.request('put', `/api/extranet/feature-flags/${corporateId}`, {}, {
                        is_point_control_enabled: newValue,
                        feature: toggledFeature,
                        flag: targetFlagStr
                    })
                }

                showToast({
                    type: 'success',
                    title: 'Changes Saved',
                    message: `${entries.length} feature flag${entries.length !== 1 ? 's' : ''} updated successfully.`
                })

                pendingChanges.value = {}
                await loadFeatureFlags()
            } catch (error) {
                console.error('Error saving feature flags:', error)
                showToast({
                    type: 'error',
                    title: 'Save Failed',
                    message: error.response?.data?.message || 'Failed to save changes'
                })
            } finally {
                isSaving.value = false
            }
        }

        async function confirmToggle() {
            if (!pendingToggle.value.flag) return
            const flag = pendingToggle.value.flag
            const flagId = flag.id
            const corporateId = flag.corporate_id || activeCorporateId.value
            const newValue = pendingToggle.value.newValue
            const toggledFeature = flag.feature

            showConfirmModal.value = false
            toggleLoading.value[flagId] = true

            try {
                let targetFlagStr = ''
                if (toggledFeature === 'point_control') {
                    targetFlagStr = newValue ? 'DECENTRALIZE' : 'NONACTIVE'
                } else {
                    targetFlagStr = newValue ? 'ACTIVE' : 'NONACTIVE'
                }

                await api.request('put', `/api/extranet/feature-flags/${corporateId}`, {}, {
                    is_point_control_enabled: newValue,
                    feature: toggledFeature,
                    flag: targetFlagStr
                })

                showToast({
                    type: 'success',
                    title: 'Flag Updated',
                    message: `Feature flag "${flag.name}" has been ${newValue ? 'enabled' : 'disabled'}.`
                })

                await loadFeatureFlags()
            } catch (error) {
                console.error('Error toggling feature flag:', error)
                showToast({
                    type: 'error',
                    title: 'Update Failed',
                    message: error.response?.data?.message || 'Failed to toggle feature flag'
                })
            } finally {
                toggleLoading.value[flagId] = false
                pendingToggle.value = { flag: null, newValue: false }
            }
        }

        function viewAudit(flagId) {
            const flag = allFlagsFlat.value.find(f => f.id === flagId)
            if (flag) {
                const targetFeature = flag.feature
                const targetCorporateId = flag.corporate_id || activeCorporateId.value
                const query = targetFeature ? { feature: targetFeature } : {}
                router.push({ 
                    name: 'ExtranetFeatureFlagAuditLog', 
                    params: { corporateId: targetCorporateId }, 
                    query 
                })
            }
        }

        function viewDetail(featureKey) {
            router.push({
                name: 'ExtranetFeatureFlagDetail',
                params: { featureKey }
            })
        }

        onMounted(() => {
            loadFeatureFlags()
        })

        watch(activeCorporateId, (newVal) => {
            pendingChanges.value = {}
            if (newVal) {
                loadFeatureFlags()
            } else {
                corporateFlags.value = []
                branchFlags.value = []
                merchantFlags.value = []
                systemFlags.value = []
            }
        })

        return {
            page,
            validation,
            globalSearchQuery,
            toggleLoading,
            toggleResetCounter,
            showConfirmModal,
            showHowItWorks,
            pendingToggle,
            pendingChanges,
            hasChanges,
            isSaving,
            activeCorporateId,
            activeCorporateName,
            filteredCorporateFlags,
            filteredBranchFlags,
            filteredMerchantFlags,
            filteredSystemFlags,
            allFlagsFlat,
            totalFlagsCount,
            hasAnyFilteredFlags,
            handleGlobalSearch,
            clearAllFilters,
            handleToggleClick,
            discardToggle,
            confirmToggle,
            discardChanges,
            saveChanges,
            viewAudit,
            viewDetail
        }
    }
}
</script>

<style scoped>
.feature-flags-management-container { max-width: 1440px; margin: 0 auto; }

.page-title { font-size: 26px; font-weight: 800; color: #0f172a; margin-bottom: 4px; letter-spacing: -0.5px; }
.page-subtitle { font-size: 14px; color: #64748b; margin: 0; }

.btn-info-filter { padding: 10px 16px; background: white; border: 1px solid var(--extranet-accent); border-radius: var(--extranet-rounded); color: #64748b; font-weight: 600; font-size: 13px; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; gap: 8px; }
.btn-info-filter:hover { color: var(--extranet-primary); border-color: var(--extranet-primary); background: #fff1f1; }

.management-card { background: white; border-radius: var(--extranet-rounded); border: 1px solid var(--extranet-accent); overflow: hidden; }

.filter-bar { padding: 24px 32px; display: flex; justify-content: space-between; align-items: flex-end; background: #ffffff; border-bottom: 1px solid #f1f5f9; gap: 24px; }
.filter-left { display: flex; align-items: flex-end; gap: 24px; flex: 1; }
.filter-group { display: flex; flex-direction: column; gap: 8px; min-width: 280px; }

.section-label-tabs { display: flex; }
.label-tab { font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.8px; }

.search-input-wrapper-ext { position: relative; }
.form-control-ext-search { width: 100%; padding: 10px 40px 10px 16px; background: #f8fafc; border: 1px solid var(--extranet-accent); border-radius: var(--extranet-rounded); font-size: 14px; font-weight: 600; outline: none; }
.form-control-ext-search:focus { border-color: var(--extranet-primary); background: white; }
.search-icon-right { position: absolute; right: 16px; top: 50%; transform: translateY(-50%); color: #94a3b8; pointer-events: none; }

.filter-right { display: flex; align-items: center; gap: 16px; }
.stats-summary { display: flex; align-items: center; gap: 8px; padding: 10px 16px; background: #f8fafc; border-radius: var(--extranet-rounded); font-size: 13px; border: 1px solid var(--extranet-accent); }
.stats-label { color: #64748b; font-weight: 600; }
.stats-value { color: var(--extranet-primary); font-weight: 800; }
.btn-reset-primary { width: 40px; height: 40px; border: 1px solid var(--extranet-accent); background: white; border-radius: var(--extranet-rounded); color: #64748b; display: flex; align-items: center; justify-content: center; cursor: pointer; }
.btn-reset-primary:hover { color: var(--extranet-primary); border-color: var(--extranet-primary); }

.card-content { min-height: 400px; background: white; padding: 0; }
.sections-container { padding: 12px; background: #ffffff; }

.loading-state { padding: 80px 32px; text-align: center; }
.loader-box { display: flex; flex-direction: column; align-items: center; gap: 16px; }
.empty-state-container { padding: 80px 32px; text-align: center; }
.no-results-state { padding: 60px 32px; text-align: center; }
.empty-art { font-size: 64px; color: #e5e7eb; margin-bottom: 24px; }

.empty-flags-container { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 32px; text-align: center; }
.empty-flags-illustration { opacity: 0.85; }
.empty-flags-title { font-size: 14px; color: #6b7280; font-weight: 400; margin: 0 0 4px; }
.empty-flags-sub { font-size: 13px; color: #9ca3af; margin: 0; }

.empty-state-container h3, .no-results-state h3 { font-size: 20px; font-weight: 700; color: #374151; margin-bottom: 8px; }
.empty-state-container p, .no-results-state p { font-size: 14px; color: #6b7280; }

.alert-box-ext { padding: 16px 24px; background: #fef2f2; border: 1px solid #fecaca; border-radius: var(--extranet-rounded); color: #dc2626; font-weight: 600; }

.confirm-modal-content { text-align: center; padding: 20px 0; }
.confirm-icon { width: 64px; height: 64px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; font-size: 24px; }
.confirm-icon.enable { background: #dcfce7; color: #166534; }
.confirm-icon.disable { background: #fee2e2; color: #991b1b; }
.flag-info-card { background: #f8fafc; border: 1px solid #e2e8f0; padding: 16px; border-radius: 12px; margin-top: 16px; }
.flag-info-key { font-weight: 800; color: #1e293b; font-size: 15px; }
.flag-info-desc { font-size: 13px; color: #64748b; margin-top: 4px; }

.btn-success-ext { background: #10b981; color: white; font-weight: 700; padding: 10px 24px; border-radius: 10px; border: none; }
.btn-danger-ext { background: #ef4444; color: white; font-weight: 700; padding: 10px 24px; border-radius: 10px; border: none; }
.btn-light-ext { background: #f1f5f9; color: #475569; font-weight: 600; padding: 10px 24px; border-radius: 10px; border: none; margin-right: 8px; }

/* Pending Changes Footer */
.pending-footer {
  position: fixed;
  bottom: 0;
  left: 232px;
  right: 0;
  z-index: 990;
  background: #1e293b;
  border-top: 2px solid var(--extranet-primary);
  padding: 14px 100px 14px 32px; 
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.15);
  transition: left 0.3s ease;
}

.sidebar-collapsed .pending-footer {
  left: 70px;
}

@media (max-width: 768px) {
  .pending-footer {
    left: 70px;
    padding: 14px 90px 14px 16px; /* Mobile adjustment */
  }
}

.pending-footer-content {
  max-width: 1440px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.pending-footer-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #e2e8f0;
  font-size: 14px;
}

.pending-footer-info i {
  color: #f59e0b;
  font-size: 16px;
}

.pending-footer-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-discard-changes {
  padding: 10px 20px;
  background: transparent;
  border: 1px solid #475569;
  border-radius: 8px;
  color: #94a3b8;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
}

.btn-discard-changes:hover:not(:disabled) {
  border-color: #94a3b8;
  color: #e2e8f0;
}

.btn-discard-changes:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-save-changes {
  padding: 10px 24px;
  background: var(--extranet-primary);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-save-changes:hover:not(:disabled) {
  background: #e05555;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(252, 102, 102, 0.35);
}

.btn-save-changes:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Footer slide transition */
.footer-slide-enter-active, .footer-slide-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.footer-slide-enter-from, .footer-slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

@keyframes slideIn { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
.scenario-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px; }
.scenario-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; padding-bottom: 12px; border-bottom: 1px solid #e2e8f0; }
.scenario-title { font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; }
.scenario-config { font-size: 11px; color: #64748b; background: white; padding: 4px 10px; border-radius: 6px; border: 1px solid #e2e8f0; }
.scenario-timeline { display: flex; flex-direction: column; gap: 16px; }
.timeline-step { display: flex; align-items: flex-start; gap: 12px; position: relative; }
.timeline-step:not(:last-child)::after { content: ''; position: absolute; left: 7px; top: 24px; width: 2px; height: calc(100% + 8px); background: #e2e8f0; }
.timeline-marker { width: 16px; height: 16px; border-radius: 50%; border: 3px solid #10b981; background: white; flex-shrink: 0; margin-top: 2px; position: relative; z-index: 1; }
.step-content { flex: 1; display: flex; flex-direction: column; gap: 4px; }
.step-label { font-size: 12px; font-weight: 700; color: #1e293b; }
.step-desc { font-size: 12px; color: #64748b; line-height: 1.5; }
.step-note { font-size: 11px; color: #64748b; background: #eaeef3; padding: 6px 10px; border-radius: 4px; border: 1px solid #e2e8f0; display: inline-block; margin-top: 4px; width: fit-content; }
</style>
